import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

interface FeatheredScrollAreaProps {
    children: ReactNode
    /** Classes for the OUTER wrapper (margins, flex sizing, etc.). */
    className?: string
    /** Classes for the scroll viewport — SET THE HEIGHT/MAX-HEIGHT HERE (e.g. "max-h-[72vh]"). */
    viewportClassName?: string
    /** Height of the top/bottom feather in px. */
    fade?: number
}

/**
 * Scroll container that tells the reader WHERE they are in long content (e.g. a runbook that's
 * tens–hundreds of lines):
 *  - the top/bottom edges FEATHER (fade to the background) only when there's more content in that
 *    direction, so a crisp/solid edge means you're at the very top or the very bottom.
 *  - a slim vertical position bar on the right mirrors the viewport within the whole document —
 *    its size = how much is on screen, its position = how far down you are.
 */
export function FeatheredScrollArea({ children, className, viewportClassName, fade = 44 }: FeatheredScrollAreaProps) {
    const viewportRef = useRef<HTMLDivElement>(null)
    const [m, setM] = useState({ top: 0, scrollH: 0, clientH: 0 })

    const measure = useCallback(() => {
        const el = viewportRef.current
        if (!el) return
        setM({ top: el.scrollTop, scrollH: el.scrollHeight, clientH: el.clientHeight })
    }, [])

    useEffect(() => {
        measure()
        const el = viewportRef.current
        if (!el || typeof ResizeObserver === 'undefined') return
        // Re-measure when the viewport OR its content resizes (async load, expanding a row, etc.).
        const ro = new ResizeObserver(measure)
        ro.observe(el)
        Array.from(el.children).forEach((c) => ro.observe(c))
        return () => ro.disconnect()
    }, [measure, children])

    const maxScroll = Math.max(0, m.scrollH - m.clientH)
    const scrollable = maxScroll > 2
    const atTop = m.top <= 1
    const atBottom = m.top >= maxScroll - 1
    const progress = scrollable ? m.top / maxScroll : 0
    const thumbH = scrollable ? Math.max(0.1, m.clientH / m.scrollH) : 1 // fraction of the track
    const thumbTop = scrollable ? progress * (1 - thumbH) : 0

    return (
        <div className={cn('relative', className)}>
            <div ref={viewportRef} onScroll={measure} className={cn('overflow-y-auto pr-4', viewportClassName)}>
                {children}
            </div>

            {/* Feathered edges — a solid edge means you've reached the top / bottom. */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 top-0 z-10 transition-opacity duration-200"
                style={{ height: fade, opacity: scrollable && !atTop ? 1 : 0, background: 'linear-gradient(to bottom, hsl(var(--background)), transparent)' }}
            />
            <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 bottom-0 z-10 transition-opacity duration-200"
                style={{ height: fade, opacity: scrollable && !atBottom ? 1 : 0, background: 'linear-gradient(to top, hsl(var(--background)), transparent)' }}
            />

            {/* Vertical position indicator — where you are in the whole document. */}
            {scrollable && (
                <div aria-hidden className="pointer-events-none absolute right-1 top-1.5 bottom-1.5 z-20 w-1.5">
                    <div className="relative h-full w-full overflow-hidden rounded-full bg-foreground/10">
                        <div
                            className="absolute inset-x-0 rounded-full bg-primary/70"
                            style={{ height: `${thumbH * 100}%`, top: `${thumbTop * 100}%` }}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}
